<template>
  <PublicLayout>
    <!-- Hero -->
    <section class="hero">
      <div class="container hero-inner">
        <div class="hero-content">
          <span class="hero-badge badge badge-info">وكالة عقارات موثوقة</span>
          <h1>اعثر على منزل أحلامك في الكويت</h1>
          <p class="hero-subtitle muted">
            تصفّح مئات العقارات للبيع والإيجار — فيلات، شقق، ومكاتب تجارية في
            أفضل المواقع.
          </p>
          <div class="hero-actions">
            <RouterLink to="/properties" class="btn btn-primary">
              تصفح العقارات
            </RouterLink>
            <RouterLink to="/areas" class="btn btn-secondary">
              استكشف المناطق
            </RouterLink>
          </div>
        </div>
        <div class="hero-image">
          <img src="@/assets/media/home.png" alt="فيلا عصرية في الكويت" />
        </div>
      </div>
    </section>

    <!-- Stats -->
    <section class="section">
      <div class="container">
        <div class="stats-grid">
          <div v-for="stat in stats" :key="stat.label" class="stat-card">
            <div class="value">{{ stat.value }}</div>
            <div class="label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Properties -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <div>
            <h2>عقارات مميزة</h2>
            <p class="muted">أحدث العروض المتاحة للبيع والإيجار</p>
          </div>
          <RouterLink to="/properties" class="btn btn-secondary"
            >عرض الكل</RouterLink
          >
        </div>
        <div class="property-grid">
          <PropertyCard
            v-for="property in featured"
            :key="property.id"
            :property="property"
          />
        </div>
      </div>
    </section>

    <!-- Areas -->
    <section class="section areas-section">
      <div class="container">
        <div class="section-header">
          <div>
            <h2>استكشف المناطق</h2>
            <p class="muted">اختر الحي المناسب لنمط حياتك</p>
          </div>
          <RouterLink to="/areas" class="btn btn-secondary"
            >كل المناطق</RouterLink
          >
        </div>
        <div class="areas-preview">
          <RouterLink
            v-for="area in previewAreas"
            :key="area.slug"
            :to="`/areas/${area.slug}`"
            class="area-preview card"
          >
            <img :src="area.image" :alt="area.name" loading="lazy" />
            <div class="area-preview-body">
              <h3>{{ area.name }}</h3>
              <span class="muted">{{ area.propertyCount }} عقار</span>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- Services -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <div>
            <h2>خدماتنا</h2>
            <p class="muted">حلول عقارية متكاملة</p>
          </div>
          <RouterLink to="/services" class="btn btn-secondary"
            >عرض الكل</RouterLink
          >
        </div>
        <div class="services-preview">
          <div
            v-for="s in previewServices"
            :key="s.id"
            class="service-preview card"
          >
            <h3>{{ s.title }}</h3>
            <p class="muted">{{ s.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <TrustBand />

    <!-- Blog -->
    <section class="section">
      <div class="container">
        <div class="section-header">
          <div>
            <h2>من مدونتنا</h2>
            <p class="muted">نصائح وأدلة لسوق العقارات</p>
          </div>
          <RouterLink to="/blog" class="btn btn-secondary"
            >كل المقالات</RouterLink
          >
        </div>
        <div class="blog-preview">
          <RouterLink
            v-for="post in previewPosts"
            :key="post.id"
            :to="`/blog/${post.slug}`"
            class="blog-preview-card card"
          >
            <img :src="post.image" :alt="post.title" loading="lazy" />
            <div class="blog-preview-body">
              <span class="badge badge-info">{{ post.category }}</span>
              <h3>{{ post.title }}</h3>
              <p class="muted">{{ post.excerpt }}</p>
            </div>
          </RouterLink>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section">
      <div class="container">
        <div class="cta-card card">
          <h2>هل تبحث عن عقار محدد؟</h2>
          <p class="muted">
            تواصل مع فريقنا المتخصص وسنساعدك في إيجاد العقار المناسب.
          </p>
          <div class="cta-actions">
            <RouterLink to="/contact" class="btn btn-primary"
              >تواصل معنا</RouterLink
            >
            <RouterLink to="/faq" class="btn btn-secondary"
              >الأسئلة الشائعة</RouterLink
            >
          </div>
        </div>
      </div>
    </section>
  </PublicLayout>
</template>

<script setup>
import { computed } from "vue";
import { RouterLink } from "vue-router";
import PublicLayout from "@/components/layout/PublicLayout.vue";
import PropertyCard from "@/components/mkdf/PropertyCard.vue";
import TrustBand from "@/components/mkdf/TrustBand.vue";
import { properties } from "@/data/properties";
import { areas } from "@/data/areas";
import { services } from "@/data/services";
import { blogPosts } from "@/data/blog";

const featured = computed(() => properties.slice(0, 4));
const previewAreas = computed(() => areas.slice(0, 4));
const previewServices = computed(() => services.slice(0, 3));
const previewPosts = computed(() => blogPosts.slice(0, 2));

const stats = [
  { value: `${properties.length}+`, label: "عقار متاح" },
  { value: "1,200+", label: "عميل سعيد" },
  { value: "15+", label: "سنة خبرة" },
  { value: String(areas.length), label: "مناطق" },
];
</script>

<style scoped>
.hero {
  padding-block: 48px 32px;
}

.hero-inner {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: center;
}

@media (min-width: 1024px) {
  .hero-inner {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }
}

.hero-badge {
  margin-bottom: 16px;
}

.hero-content h1 {
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  line-height: 1.25;
  margin-bottom: 16px;
}

.hero-subtitle {
  font-size: 1.0625rem;
  max-width: 480px;
  margin-bottom: 24px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hero-image {
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.hero-image img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.areas-preview {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--section-gap);
}

@media (min-width: 1024px) {
  .areas-preview {
    grid-template-columns: repeat(4, 1fr);
  }
}

.area-preview {
  padding: 0;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform var(--motion), box-shadow var(--motion);
}

.area-preview:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.area-preview img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
}

.area-preview-body {
  padding: 16px;
}

.area-preview-body h3 {
  font-size: 1rem;
  margin-bottom: 4px;
}

.services-preview {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--section-gap);
}

@media (min-width: 768px) {
  .services-preview {
    grid-template-columns: repeat(3, 1fr);
  }
}

.service-preview h3 {
  font-size: 1rem;
  margin-bottom: 8px;
}

.service-preview p {
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.blog-preview {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--section-gap);
}

@media (min-width: 768px) {
  .blog-preview {
    grid-template-columns: repeat(2, 1fr);
  }
}

.blog-preview-card {
  padding: 0;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform var(--motion), box-shadow var(--motion);
}

.blog-preview-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.blog-preview-card img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.blog-preview-body {
  padding: 20px 24px;
}

.blog-preview-body h3 {
  font-size: 1rem;
  margin: 8px 0;
  line-height: 1.4;
}

.blog-preview-body p {
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
}

.cta-card {
  text-align: center;
  max-width: 640px;
  margin-inline: auto;
}

.cta-card h2 {
  font-size: 1.375rem;
  margin-bottom: 12px;
}

.cta-card > p {
  margin-bottom: 24px;
}

.cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}
</style>
